import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Loader2, AlertCircle, Download, ArrowRight, CheckCircle2} from 'lucide-react'
import { baixarCertificadoPdf } from '@/utils/certificadoPdf'

/**
 * Rota pública /certificado/download — Diagnóstico Saúde e Emoções
 *
 * Acessada a partir do link "Baixar meu certificado" enviado por e-mail.
 * Verifica `email` + `token` na URL contra a coleção `capacitacao` e,
 * se válidos, gera o PDF do certificado e dispara download automático
 * SEM exigir sessão ativa de login.
 *
 * URL: /certificado/download?email=...&token=...
 */
function CertificadoDownload() {
  const [status, setStatus] = useState<'verificando' | 'pronto' | 'baixando' | 'sucesso' | 'erro'>('verificando')
  const [erro, setErro] = useState('')
  const [nomeAluno, setNomeAluno] = useState('')
  const [dataIsoAluno, setDataIsoAluno] = useState('')

  useEffect(() => {
    void verificarToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Apenas valida o token e prepara os dados — NÃO dispara download automático.
  // Download automático ao abrir link de e-mail é bloqueado pelos navegadores
  // por falta de interação prévia do usuário na aba.
  async function verificarToken() {
    try {
      const params = new URLSearchParams(window.location.search)
      const email = params.get('email')?.trim() ?? ''
      const token = params.get('token')?.trim() ?? ''

      if (!email || !token) {
        setStatus('erro')
        setErro('Link inválido. Verifique se o link do e-mail foi copiado completo.')
        return
      }

      // Buscar registro pelo e-mail
      const resp = await lumi.entities.capacitacao.list({
        filter: { email },
        limit: 1,
      })
      const item = resp?.list?.[0]

      if (!item) {
        setStatus('erro')
        setErro('Não encontramos sua inscrição. Verifique seu e-mail ou refaça o cadastro na seleção.')
        return
      }

      // Validar token — aceita tokenCriarSenha (campo dedicado do e-mail de conclusão)
      // ou tokenAcesso como fallback para links gerados antes da separação dos campos
      const tokenCriarSenha = item.tokenCriarSenha ?? ''
      const tokenAcesso = item.tokenAcesso ?? ''
      const tokenValido = (tokenCriarSenha && tokenCriarSenha === token) ||
                          (tokenAcesso && tokenAcesso === token)
      if (!tokenValido) {
        setStatus('erro')
        setErro('Link inválido ou expirado. Acesse o último e-mail recebido para baixar seu certificado.')
        return
      }

      // Validar conclusão do curso
      if (!item.certificadoEmitido && item.etapaAtual !== 'certificado_emitido' && item.etapaAtual !== 'acesso_criado') {
        setStatus('erro')
        setErro('Conclua todas as 7 aulas do curso para poder baixar seu certificado.')
        return
      }

      const nome = item.nome ?? 'Aluno(a)'
      const dataIso = item.dataCertificado || new Date().toISOString()
      setNomeAluno(nome)
      setDataIsoAluno(dataIso)

      // Token válido → mostrar botão para o usuário clicar e baixar
      setStatus('pronto')
    } catch (e) {
      console.error('[certificado-download] erro', e)
      setStatus('erro')
      setErro('Ocorreu um erro ao processar seu certificado. Tente novamente em instantes.')
    }
  }

  // Download disparado por clique do usuário (evita bloqueio do navegador)
  async function executarDownload() {
    if (!nomeAluno) return
    setStatus('baixando')
    try {
      await baixarCertificadoPdf(nomeAluno, dataIsoAluno || new Date().toISOString())
      setStatus('sucesso')
    } catch (e) {
      console.error('[certificado-download] erro ao gerar PDF', e)
      setStatus('pronto') // volta ao estado pronto para tentar de novo
    }
  }

  async function tentarNovamente() {
    if (!nomeAluno) return
    setStatus('baixando')
    try {
      await baixarCertificadoPdf(nomeAluno, dataIsoAluno || new Date().toISOString())
      setStatus('sucesso')
    } catch (e) {
      console.error('[certificado-download] erro ao re-baixar', e)
      setStatus('pronto')
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 sm:p-10 border border-[#EDE0D4] shadow-sm text-center">
        {status === 'verificando' && (
          <>
            <Loader2 className="mx-auto animate-spin text-[#2D6A4F] mb-4" size={36} />
            <p className="font-cormorant text-xl font-semibold text-[#2D6A4F] mb-2">
              Verificando seu certificado…
            </p>
            <p className="text-sm text-[#5F5E5A]">Aguarde só um instante.</p>
          </>
        )}

        {status === 'pronto' && (
          <>
            <CheckCircle2 className="mx-auto text-[#52B788] mb-4" size={42} />
            <p className="font-cormorant text-2xl font-semibold text-[#2D6A4F] mb-2">
              Certificado pronto!
            </p>
            <p className="text-sm text-[#2C2C2A] mb-6">
              Seu certificado de <strong>Aromaterapia Introdutória</strong> está disponível.<br />
              Clique no botão abaixo para baixar o PDF.
            </p>
            <button
              type="button"
              onClick={executarDownload}
              className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors mb-4"
            >
              <Download size={18} />
              Baixar meu certificado em PDF
            </button>
            <div>
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 text-sm text-[#2D6A4F] hover:underline"
              >
                Acessar meu painel
                <ArrowRight size={14} />
              </Link>
            </div>
          </>
        )}

        {status === 'baixando' && (
          <>
            <Loader2 className="mx-auto animate-spin text-[#2D6A4F] mb-4" size={36} />
            <p className="font-cormorant text-xl font-semibold text-[#2D6A4F] mb-2">
              Gerando seu PDF…
            </p>
            <p className="text-sm text-[#5F5E5A]">Aguarde, preparando o download…</p>
          </>
        )}

        {status === 'sucesso' && (
          <>
            <CheckCircle2 className="mx-auto text-[#2D6A4F] mb-4" size={42} />
            <p className="font-cormorant text-2xl font-semibold text-[#2D6A4F] mb-2">
              Pronto!
            </p>
            <p className="text-sm text-[#2C2C2A] mb-6">
              Seu certificado foi baixado com sucesso. Verifique a pasta de downloads do seu dispositivo.
            </p>
            <button
              type="button"
              onClick={tentarNovamente}
              className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors mb-3"
            >
              <Download size={18} />
              Baixar novamente
            </button>
            <div>
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 text-sm text-[#2D6A4F] hover:underline"
              >
                Acessar meu painel
                <ArrowRight size={14} />
              </Link>
            </div>
          </>
        )}

        {status === 'erro' && (
          <>
            <AlertCircle className="mx-auto text-[#B08968] mb-4" size={42} />
            <p className="font-cormorant text-xl font-semibold text-[#2D6A4F] mb-2">
              Não foi possível baixar
            </p>
            <p className="text-sm text-[#2C2C2A] mb-6">{erro}</p>
            <Link
              to="/curso"
              className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-3 rounded-full hover:bg-[#1f4f3a] transition-colors"
            >
              Ir para o curso
              <ArrowRight size={18} />
            </Link>
          </>
        )}
      </div>
    </div>
  )
}

export default CertificadoDownload
